import React, { useContext } from "react";
import { paperUrl } from "../constants";
import { LinkButton } from "../components/Card";
import { ThemeContext } from "../themeProvider";
import { motion } from "framer-motion";

const highlights = [
  "Quantum-native autoregressive language modelling: byte-level models whose state, dynamics and sampling are all quantum, with next-byte probabilities read straight from measurements instead of a classical softmax.",
  "Learned dissipative memory: purely unitary memory cannot reset what it stores, while learned, token-conditioned dissipation gives memory that helps beyond one token.",
  "Capacity limits: on a key–value recall benchmark, one stored pair is solved at 4–5 memory qubits, two at 6–7, and three remain unsolved.",
  "Hardware validation on a 156-qubit IBM Heron processor: measured memory-register distributions show signal against a uniform baseline in 12 of 12 circuits, and bytes are generated by sampling physically measured probabilities.",
  "Compared against retrained, parameter-matched classical controls and n-gram baselines, under a pre-registered, falsification-first protocol.",
];

const Research = () => {
  const theme = useContext(ThemeContext);

  return (
    <div id="research">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 px-4 pt-24 pb-12">
        <div style={{ width: "100%", alignItems: "center", justifyContent: "center", alignContent: "center", display: "flex" }}>
          <h2
            className={
              theme.state.darkMode
                ? "text-5xl font-bold px-4 md:px-0 text-center colored-bottom-line"
                : "text-5xl font-bold px-4 md:px-0 text-center text-white colored-bottom-line"
            }
          >
            Research
          </h2>
        </div>

        <h4 className="mt-16 text-3xl font-semibold multicolortext" style={{ width: "max-content", maxWidth: "100%" }}>
          Featured Publication
        </h4>

        <motion.div
          initial="hidden"
          whileInView={"visible"}
          variants={{
            visible: {
              opacity: 1,
              scale: 1,
              transition: {
                type: "spring",
              },
            },
            hidden: { opacity: 0, scale: 0.8 },
          }}
          className={
            theme.state.darkMode
              ? "mt-8 bg-white rounded-lg p-6 shadow-lg"
              : "mt-8 bg-gray-800 rounded-lg p-6 shadow-lg"
          }
        >
          <div className="flex flex-col md:flex-row justify-between md:items-start md:gap-8">
            <h3 className={
              theme.state.darkMode
                ? "text-2xl font-bold text-black"
                : "text-2xl font-bold text-white"
            }>
              Trainable Quantum-Native Language Models: Dissipative Memory, Capacity Limits, and Hardware Validation
            </h3>
            <span className="text-blue-500 font-semibold md:whitespace-nowrap mt-2 md:mt-1">October 2026 · arXiv preprint</span>
          </div>
          <h4 className="text-xl mt-2 font-semibold multicolortext" style={{ width: "max-content", maxWidth: "100%" }}>
            Deniz Yunus Göğüş
          </h4>
          <p className={theme.state.darkMode ? "mt-1 text-sm text-gray-500" : "mt-1 text-sm text-gray-400"}>
            arXiv:2610.12806 · Quantum Physics (quant-ph), Machine Learning (cs.LG)
          </p>
          <p className={
            theme.state.darkMode
              ? "mt-4 text-gray-600"
              : "mt-4 text-gray-300"
          }>
            I investigated whether a language model can keep and update its memory directly inside a quantum system, instead of using quantum circuits only as feature extractors. The model's hidden state is a small quantum register, each token applies a learned quantum channel to it, and generation runs autoregressively from measurement outcomes. The work covers trainable quantum channels, dissipative memory dynamics and capacity limits, and validates the model's behaviour on IBM quantum hardware.
          </p>
          <div className="mt-4 space-y-2">
            {highlights.map((highlight) => (
              <div key={highlight} className={
                theme.state.darkMode
                  ? "text-gray-600"
                  : "text-gray-300"
              }>
                • {highlight}
              </div>
            ))}
          </div>
          <p className={
            theme.state.darkMode
              ? "mt-4 text-gray-500 italic"
              : "mt-4 text-gray-400 italic"
          }>
            What it doesn't claim: the models are classically simulable at these sizes and substantially weaker than classical n-gram baselines, and the paper claims no quantum computational advantage. It is a preprint and has not been peer reviewed.
          </p>
          <div className="mt-6">
            <LinkButton href={paperUrl}>Read Paper</LinkButton>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Research;
